/* eslint-disable import/order */
/* eslint-disable import/no-unresolved */
import cx from 'classnames';
import React, {
  MouseEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import { Spinner, UncontrolledTooltip } from 'reactstrap';
import ListViewItem from '../../ListViewItem';
import TypeSelectModal from '../../TypeSelectModal';
import { Product } from '~domain/product/Product';
import { accountSelector } from '~redux/account/selectors/account';
import { authUserSelector } from '~redux/auth/selectors/authUser';
import { Brand, Product as TProduct, ProductType } from '~redux/brand/types';
import { buyerInfoSelector } from '~redux/buyer/selectors';
import { addCart as addCartThunk } from '~redux/cart/thunk';
import {
  addDefaultFavoriteProduct as addDefaultFavoriteProductThunk,
  removeFavoriteProduct as removeFavoriteProductThunk,
} from '~redux/favoriteproduct/thunk';
import { formatCurrency } from '~utils/formatter';
import { useThunk } from '~utils/hooks';
import './styles.scss';
import { pushAddToCartEvent, pushSelectItemEvent } from '~utils/gtm';
import { isProtectedBrand } from '~utils/brand';
import qs from 'querystring';
import { productClicked } from '~utils/segment';
import { signup } from '~redux/auth/reducers/signup';
import { getPublicImageUrl } from '~utils/image';
import {
  useSelectInventoryByProduct,
  useSelectIsOutOfStockByProduct,
} from '~view/hooks/inventory';
import FavoriteListDialog from '~view/widgets/FavoriteListDialog';
import { Button, Chip } from '@material-ui/core';
import { makeSelectFavoriteProductId } from '~redux/favorite/reducers/product';
import { FiShoppingCart } from '@react-icons/all-files/fi/FiShoppingCart';

type ItemProps = {
  product: TProduct;
  productTypes?: ProductType[];
  border?: boolean;
  index: number;
  col?: boolean;
  showBrand?: boolean;
  unAuth?: boolean;
  isLongListView?: boolean;
  hideBadge?: boolean;
  image?: string;
  moveFavoriteList?: boolean;
};

const SEASON_LABEL: Record<string, string> = {
  AW: 'A/W',
  SS: 'S/S',
};

type ButtonAction = 'cart' | 'option' | 'collection';
const BUTTON_ACTION_LABEL: Record<ButtonAction, string> = {
  cart: 'カートに追加',
  option: 'サイズなどを見る',
  collection: 'コレクションを見る',
};

const Item: React.FC<ItemProps> = ({
  product: productJson,
  productTypes,
  border,
  index,
  col = true,
  showBrand = true,
  unAuth,
  isLongListView = false,
  hideBadge,
  image,
  moveFavoriteList,
}) => {
  const dispatch = useDispatch();
  const user = useSelector(authUserSelector);
  const account = useSelector(accountSelector)!;
  const buyerInfo = useSelector(buyerInfoSelector)!;
  const history = useHistory();
  const is_direct = !!qs.parse(history.location.search?.split('?')?.[1])
    ?.direct;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [addCart, loading, complete] = useThunk(addCartThunk);
  const product = useMemo(() => Product.create(productJson), [productJson]);
  const productUrl = useMemo(() => {
    const url = new URL(location.href);
    url.searchParams.delete('page');
    return `/productdetail/${encodeURIComponent(product.id)}/${url.search}`;
  }, [product.id]);
  const brandUrl = useMemo(() => {
    const url = new URL(location.href);
    url.searchParams.delete('page');
    return `/brand/${product.product_brand_id}${url.search}`;
  }, [product.product_brand_id]);
  const inventory = useSelectInventoryByProduct(product.id);
  const outOfStock = useSelectIsOutOfStockByProduct(product);
  // 予約販売は売り切れにしない
  const isOutOfStock = useMemo(
    () => product.product_preorder !== 1 && outOfStock,
    [product.product_preorder, outOfStock]
  );

  const [addDefaultFavoriteProduct, addLoading] = useThunk(
    addDefaultFavoriteProductThunk
  );
  const [removeFavoriteProduct, removeLoading] = useThunk(
    removeFavoriteProductThunk
  );

  const handleClick = useCallback<MouseEventHandler>(
    (e) => {
      if (!user && !unAuth && !is_direct) {
        e.preventDefault();
        e.stopPropagation();
      }

      productClicked({
        brand: product.brand.brand_name,
        brand_id: product.product_brand_id,
        category: product.product_category,
        category2: product.product_subcategory,
        image_url: product.imageUrl,
        name: product.product_name,
        position: index + 1,
        price: product.unitPrice,
        product_id: product.id,
        sku: product.product_number,
        url: `${document.location.protocol}//${document.location.host}/productdetail/${product.id}`,
      });

      if (!user && !unAuth && !is_direct) {
        dispatch(
          signup({
            nextPath: productUrl,
            referralId: is_direct ? product.product_brand_id : undefined,
          })
        );
        return;
      }
    },
    [user, product, index, history, productUrl, is_direct, unAuth]
  );

  const handleAddToCart: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (!product || !user) {
        return;
      }
      productClicked({
        brand: product.brand.brand_name,
        brand_id: product.product_brand_id,
        category: product.product_category,
        category2: product.product_subcategory,
        image_url: product.imageUrl,
        name: product.product_name,
        position: index + 1,
        price: product.unitPrice,
        product_id: product.id,
        quantity: 1,
        sku: product.product_number,
        url: `${document.location.protocol}//${document.location.host}/productdetail/${product.id}`,
      });
      if (product?.brand?.is_sale_brand) {
        history.push(productUrl);
      } else if (productTypes?.length) {
        setIsModalOpen(true);
      } else {
        addToCart(
          Math.max(
            product.product_minimum_quantity ?? 1,
            product.product_order_lot ?? 1
          )
        );
      }
      pushSelectItemEvent(
        [
          {
            item_name: product.product_name,
            item_id: product.id,
            price: product.unitPrice,
            item_brand: product.brand.brand_name,
            item_category: product.product_category,
            item_category2: product.product_subcategory,
            item_list_name: document.title,
            item_list_id: document.location.pathname,
            index,
            quantity: 1,
          },
        ],
        user
      );
    },
    [product, productTypes, user]
  );

  const addToCart = useCallback(
    (quantity: number) => {
      if (!product || !user) {
        return;
      }
      addCart(buyerInfo, {
        product,
        product_id: product.id,
        quantity,
        owner: user.attributes.sub,
        brand_id: product.product_brand_id,
        is_direct,
      });
      pushAddToCartEvent(
        [
          {
            item_name: product.product_name,
            item_id: product.id,
            price: product.unitPrice,
            item_brand: product.brand.brand_name,
            item_category: product.product_category,
            item_category2: product.product_subcategory,
            item_list_name: document.title,
            item_list_id: document.location.pathname,
            index,
            quantity,
          },
        ],
        user
      );
    },
    [product, user]
  );

  const buttonAction = useMemo<ButtonAction>(() => {
    return product?.brand?.is_sale_brand
      ? 'collection'
      : productTypes?.length
      ? 'option'
      : 'cart';
  }, [product?.brand?.is_sale_brand, productTypes?.length]);
  const [message, setMessage] = useState(BUTTON_ACTION_LABEL[buttonAction]);

  useEffect(() => {
    if (loading) {
      setMessage(BUTTON_ACTION_LABEL[buttonAction]);
      return;
    }
    if (complete) {
      setMessage('追加しました！');
      setTimeout(() => setMessage(BUTTON_ACTION_LABEL[buttonAction]), 1000);
      return;
    }
    if (isOutOfStock && buttonAction === 'cart') {
      setMessage('売り切れ');
      return;
    }
    setMessage(BUTTON_ACTION_LABEL[buttonAction]);
  }, [loading, complete, buttonAction, isOutOfStock]);

  const isProtected = useMemo(
    () =>
      user?.attributes.profile === 'buyer' &&
      account &&
      isProtectedBrand(product.brand as Brand, account),
    [product, account, user]
  );

  const isNewItem = useMemo(() => {
    const dt = new Date();
    const newItemDueDt = new Date();
    newItemDueDt.setDate(newItemDueDt.getDate() - 30);
    if (!product.publishedAt) return;
    const productCreated: Date = new Date(product.publishedAt);
    return newItemDueDt <= productCreated && productCreated <= dt;
  }, [product]);

  const [isHover, setHover] = useState(false);
  const [isFavoritHover, setFavoriteHover] = useState(false);

  const favoriteProductId = useSelector(
    makeSelectFavoriteProductId(product.id)
  );

  const handleFavoriteAdd: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    async (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (!product || !user) {
        return;
      }
      if (!favoriteProductId) {
        await addDefaultFavoriteProduct(account.id, product);
        return;
      }
      removeFavoriteProduct(favoriteProductId);
    },
    [product, favoriteProductId, user]
  );

  return (
    <ListViewItem
      className={cx({
        'product-list-view-item-small': !isLongListView,
        'product-long-list-view-item-small': isLongListView,
        'inner-border': border,
      })}
      col={col}
    >
      {moveFavoriteList && (
        <div className="favorite-drop-down-item">
          <FavoriteListDialog product={product} />
        </div>
      )}
      <Link
        className="item-link"
        onClick={(e) => (!isFavoritHover ? handleClick(e) : null)}
        title={product.product_name}
        to={productUrl}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <div
          className={cx({
            'product-list-view-item-image': true,
            'border-bottom': border,
          })}
        >
          {user && (
            <span
              className="product-list-view-item-follow-link"
              onClick={handleFavoriteAdd}
              onMouseEnter={() => setFavoriteHover(true)}
              onMouseLeave={() => setFavoriteHover(false)}
              // @ts-ignore
              disabled={addLoading || removeLoading}
            >
              <i
                className={cx({
                  'product-list-view-item-heart-icon': true,
                  followed: !!favoriteProductId,
                })}
              />
            </span>
          )}
          <img
            src={
              image ??
              getPublicImageUrl(
                product.imageKeys?.[0]?.imageKey ??
                  (product.imageKeys as any)?.items?.[0]?.imageKey,
                { w: 400, h: 400 }
              )
            }
            alt={product.product_name}
            className={`thumbnail-product${border ? '' : ' border'} bg-gray`}
            loading="lazy"
          />
          {isOutOfStock && (
            <div
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'absolute',
                top: '0px',
                left: '0px',
                background: 'rgba(255, 255, 255, 0.8)',
              }}
            >
              <p
                style={{
                  margin: '0px',
                  padding: '0px',
                  display: 'block',
                  fontWeight: 600,
                  fontSize: '16px',
                  lineHeight: '20px',
                  letterSpacing: '0.15px',
                  color: 'rgb(51, 51, 51)',
                  textTransform: 'uppercase',
                }}
              >
                売り切れ
              </p>
            </div>
          )}
          {isHover && user?.attributes.profile === 'buyer' && !isProtected && (
            <div
              className="d-flex align-items-center w-100 p-2"
              style={{ position: 'absolute', bottom: '0' }}
            >
              <Button
                variant="contained"
                color="primary"
                disabled={loading || (isOutOfStock && buttonAction === 'cart')}
                onClick={handleAddToCart}
                disableElevation
                className="rounded-pill w-100"
              >
                {loading && <Spinner size="sm" />}
                {!loading && buttonAction === 'cart' && (
                  <FiShoppingCart className="mr-2" />
                )}
                {message}
              </Button>
            </div>
          )}
        </div>
        <div className={`information py-2${border ? ' px-3' : ''}`}>
          <div className="item-title">
            <div>
              {isNewItem && <span className="mr-2 text-black-50">New</span>}
              {/* {product.product_preorder === 1 && (
              <Chip label="予約販売" className="mr-2" size="small" />
            )}
            {(!!product.product_year || product.product_season) && (
              <Chip
                label={`${
                  product.product_year ? `${product.product_year}` : ''
                }${SEASON_LABEL[product.product_season] ?? ''}`}
                className="mr-2"
                size="small"
              />
            )} */}
            </div>
            {showBrand && (
              <Link
                className="product-list-view-item-brand font-weight-bold"
                to={brandUrl}
                translate="no"
              >
                {product.brand.brand_name}
              </Link>
            )}
            <div className="font-weight-light">{product.product_name}</div>
          </div>
          <div className="mb-0">
            {user && !isProtected ? (
              <>
                <div>
                  <span className="font-weight-bold">
                    {!!product.productSaleWholesaleRate && (
                      <s className="mr-2">
                        {formatCurrency(product.originalUnitPrice)}
                      </s>
                    )}
                    <span
                      className={
                        product.productSaleWholesaleRate ? 'text-danger' : ''
                      }
                    >
                      {formatCurrency(product.unitPrice)}
                      {!product.is_open_price &&
                        `（${
                          product.productSaleWholesaleRate ??
                          product.productWholesaleRate
                        }
                        %）`}
                    </span>
                  </span>
                  {!product.is_open_price && (
                    <small className="text-muted">
                      上代 {formatCurrency(product.product_retail_price)}
                    </small>
                  )}
                  {product.is_open_price && (
                    <small className="text-muted">オープン価格</small>
                  )}
                </div>
                {!showBrand && (
                  <div className="d-flex">
                    <small className="mr-2">
                      発注単位：{product.product_order_lot ?? 1}
                    </small>
                    {!!inventory && inventory < 10 && (
                      <i className="text-muted">
                        <small>残り{inventory}点</small>
                      </i>
                    )}
                  </div>
                )}
              </>
            ) : (
              <a onClick={handleClick}>
                <span id={`p-${index}`}>
                  希望小売価格：{formatCurrency(product.product_retail_price)}
                  <i className="lock-icon" />
                </span>
                <UncontrolledTooltip target={`p-${index}`} placement="top">
                  バイヤー登録して卸価格を表示
                </UncontrolledTooltip>
              </a>
            )}
          </div>
        </div>
      </Link>
      <div className="description">{product.product_content}</div>
      {isModalOpen && (
        <TypeSelectModal
          productId={product.id}
          isOpen={isModalOpen}
          toggle={() => setIsModalOpen((prev) => !prev)}
          index={index}
        />
      )}
    </ListViewItem>
  );
};

type ProductListViewItemProps = {
  product?: TProduct;
  productTypes?: ProductType[];
  border?: boolean;
  index?: number;
  col?: boolean;
  showBrand?: boolean;
  unAuth?: boolean;
  isLongListView?: boolean;
  hideBadge?: boolean;
};

const ProductListViewSmallItem: React.FC<ProductListViewItemProps> = ({
  product,
  border,
  col = true,
  isLongListView = false,
  index,
  showBrand = true,
  ...props
}) => {
  if (!product) {
    return (
      <ListViewItem
        className={cx({
          'product-list-view-item': !isLongListView,
          'product-long-list-view-item': isLongListView,
          'inner-border': border,
        })}
        col={col}
      >
        <div className="product-list-view-item-image" />
        <div
          className={`information py-3${border ? ' p-3 border-bottom' : ''}`}
        >
          <div className="item-title mb-2" />
          <div className="item-title" style={{ width: '100px' }} />
        </div>
        {showBrand && (
          <div className={`information py-3${border ? ' p-3' : ''}`}>
            <div className="item-title mb-2" />
            <div className="item-title" style={{ width: '100px' }} />
          </div>
        )}
      </ListViewItem>
    );
  }
  return (
    <Item
      product={product}
      border={border}
      col={col}
      isLongListView={isLongListView}
      index={index!}
      showBrand={showBrand}
      {...props}
    />
  );
};

export default ProductListViewSmallItem;
